// @ts-check
/**
 * As Seen On (v2) — all JavaScript for sections/as-seen-on-v2.liquid. VEN-161.
 *
 * THIS FILE IS THE SOURCE. It is hand-written plain JS like every other script
 * in this theme — there is no build step, no bundler and no TypeScript to
 * regenerate it from. Edit it directly.
 *
 * It owns two things:
 *
 * 1. All interactive behavior of the section (tiles, pop-up, slides, dots,
 *    swipe, play/pause, focus trap). The DOM contract it relies on
 *    (`open`, `data-active`, `aria-current`, `aria-pressed`) is what
 *    assets/as-seen-on-v2.css selects on, so it must stay exactly as it is.
 * 2. All of its content: the Liquid renders empty row and pop-up shells and
 *    this file fills them, at runtime, with the media the Tolstoy getAssets
 *    API returns for the current product. When Tolstoy has nothing for that
 *    product the section hides itself on the storefront.
 *
 * The JSDoc @typedef blocks below are the only written record of the
 * undocumented `ai-widgets/v1` response shapes (reverse-engineered, see the
 * VEN-161 research notes). Keep them in sync with reality.
 *
 * Optional type-check (no config file needed, run from the repo root):
 *
 *   npx -y -p typescript@5.6 tsc --noEmit --checkJs --target es2019 \
 *     --lib es2019,dom,dom.iterable assets/as-seen-on-tolstoy.js
 */

(function () {
  'use strict';

  /* ==========================================================================
     Types — the Tolstoy API contracts (undocumented; reverse-engineered)
     ========================================================================== */

  /**
   * Per-store identity values. Always configuration — never hardcode.
   * `appKey` is the Tolstoy account key, `widgetId` the widget/publish
   * configuration, `appUrl` the store's myshopify domain.
   * @typedef {{appKey: string, widgetId: string, appUrl: string, apiBase?: string, apiBaseFallback?: string}} TolstoyConfig
   */

  /**
   * getAssets query parameters. `from`/`size` paginate, `contextProductId`
   * filters media to the current PDP, `tags` is sent as a JSON array,
   * `settingsId` selects an optional media preset.
   * @typedef {{from?: number, size?: number, contextProductId?: string, tags?: string[], settingsId?: string}} FetchAssetsParams
   */

  /**
   * Progressive MP4 renditions plus the optional HLS manifest (m3u8), which
   * only plays natively in Safari/iOS.
   * @typedef {{'640': string, '960': string, '1280': string, '480'?: string, '720'?: string, '1080'?: string, hls?: string}} VideoSources
   */

  /**
   * Video poster renditions. 1280/1920 are not always present.
   * @typedef {{'250': string, '480': string, '960': string, '1280'?: string, '1920'?: string}} VideoPoster
   */

  /**
   * Image asset renditions.
   * @typedef {{'250': string, '480': string, '960': string, '1280': string, '1920': string}} ImageSources
   */

  /**
   * The creator this media belongs to — the source of the handle chip.
   * Null on plenty of real assets (all 5 on the first configured product), in
   * which case there is no handle to show: `name` is the upload filename, not a
   * handle, and must never be used as one.
   * @typedef {{handle: string, provider: string|null, url: string|null}} CreatorProfile
   */

  /**
   * `name` is the upload filename, e.g. "VENROYES26_D1_4x5_EDEN_1_NO_LOGO_4.mp4"
   * — not a handle. `productIds` are Shopify product ids as strings: the only
   * linkage between a story and the products it shows.
   * @typedef {{type: 'video', id: string, name: string, creatorProfile: CreatorProfile|null, sources: VideoSources, poster: VideoPoster, captionsUrl: string|null, productIds?: string[]}} VideoAsset
   */

  /**
   * Image asset. `name` is the upload filename — see VideoAsset.
   * @typedef {{type: 'image', id: string, name: string, creatorProfile: CreatorProfile|null, sources: ImageSources, productIds?: string[]}} ImageAsset
   */

  /** @typedef {VideoAsset | ImageAsset} Asset */

  /** @typedef {{assets: Asset[], hasMore: boolean}} FetchAssetsResult */

  /**
   * Variant price as returned by getProducts — string or number depending on
   * the source.
   * @typedef {{price?: string|number|null}} TolstoyProductVariant
   */

  /**
   * A product record from getProducts, trimmed to what the card renders.
   * `externalProductId` matches the entries of `asset.productIds`; `handle` is
   * preferred over `productUrl`, which points at the myshopify domain;
   * `imageUrl` is a Shopify CDN URL resized by appending `?width=`.
   * @typedef {{externalProductId: string, title?: string|null, handle?: string|null, productUrl?: string|null, imageUrl?: string|null, currencyCode?: string|null, currencySymbol?: string|null, variants?: TolstoyProductVariant[]|null}} TolstoyProduct
   */

  /** @typedef {{products: TolstoyProduct[]}} FetchProductsResult */

  /* ---------------------------------------------------------- local types --- */

  /** @typedef {() => void} Teardown */

  /**
   * Per-store identity + page context, read from the root's data attributes.
   * `maxItems` is the "Maximum items" section setting (the row is a carousel,
   * not a wrapping grid); `currency` is the shop's primary ISO code, used only
   * when the API omits a product's own currencyCode — see formatPrice.
   * @typedef {{appKey: string, widgetId: string, settingsId: string, appUrl: string, productId: string, tags: string[], maxItems: number, currency: string}} SectionConfig
   */

  /**
   * Everything the render pass needs beyond the assets themselves.
   * @typedef {{currency: string}} RenderOptions
   */

  /**
   * @typedef {object} SectionController
   * @property {() => void} bind (Re)queries the DOM and binds every element listener. Idempotent.
   * @property {() => void} destroy Removes every listener it owns and releases the body lock.
   * @property {() => boolean} isDestroyed True once destroyed — an in-flight fetch must not write into a dead root.
   */

  /**
   * Shopify sets designMode in the theme editor preview; __venroyAsoInit is our
   * own execution guard (see the bootstrap at the bottom).
   * @typedef {Window & {Shopify?: {designMode?: boolean}, __venroyAsoInit?: true}} AsoWindow
   */

  /* ==========================================================================
     Constants
     ========================================================================== */

  var PRIMARY_API_BASE = 'https://cf-apilb.gotolstoy.com';
  var FALLBACK_API_BASE = 'https://apilb.gotolstoy.com';

  var ROOT_SELECTOR = '[data-aso-root]';
  var FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /** Same page size as the reference stories row. */
  var PAGE_SIZE = 24;
  /** Fallback when the section has no "Maximum items" value to slice by. */
  var DEFAULT_MAX_ITEMS = 8;
  /** How many product cards one pop-up item may show. */
  var MAX_CARDS_PER_SET = 4;
  /** The API rejects longer id lists — the reference batches at the same size. */
  var MAX_IDS_PER_REQUEST = 50;
  /** Shopify CDN resize width for product card images. */
  var PRODUCT_IMAGE_WIDTH = 600;

  /* the literal type is what makes createElementNS return SVGElement */
  /** @type {'http://www.w3.org/2000/svg'} */
  var SVG_NS = 'http://www.w3.org/2000/svg';
  var DEFAULT_TILE_LABEL = 'As Seen On';
  /** Carries a slide's captions URL until that slide is first opened. */
  var CAPTIONS_ATTRIBUTE = 'data-aso-captions';

  /** Shopify's money_format placeholders — same set as snippets/get-the-look.liquid. */
  var MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  /* ==========================================================================
     1. getAssets client
     The appKey is deliberately in the URL path (not the query) so the Tolstoy
     CDN can purge one account's cache by path pattern. The endpoint is
     unauthenticated and CORS-open.
     ========================================================================== */

  /**
   * @param {string} base
   * @param {TolstoyConfig} config
   * @param {FetchAssetsParams} params
   * @returns {string}
   */
  function buildAssetsUrl(base, config, params) {
    var from = typeof params.from === 'number' ? params.from : 0;
    var size = typeof params.size === 'number' ? params.size : PAGE_SIZE;

    var query = new URLSearchParams({
      appUrl: config.appUrl,
      widgetId: config.widgetId,
      from: String(from),
      size: String(size)
    });

    if (params.contextProductId) query.set('contextProductId', params.contextProductId);
    if (params.tags && params.tags.length > 0) query.set('tags', JSON.stringify(params.tags));
    if (params.settingsId) query.set('settingsId', params.settingsId);

    return base + '/ai-widgets/v1/media/getAssets/' + encodeURIComponent(config.appKey) + '?' + query;
  }

  /**
   * Fetches one page of media assets.
   *
   * Tries the primary (CDN) host first; on a network error or non-OK response
   * it retries once against the fallback host before giving up.
   *
   * @param {TolstoyConfig} config
   * @param {FetchAssetsParams} params
   * @returns {Promise<FetchAssetsResult>}
   */
  async function fetchAssets(config, params) {
    var primary = config.apiBase || PRIMARY_API_BASE;
    var fallbackHost = config.apiBaseFallback || FALLBACK_API_BASE;

    try {
      var first = await fetch(buildAssetsUrl(primary, config, params));
      if (first.ok) return await first.json();
    } catch (error) {
      // Network error on the primary host — fall through to the fallback.
    }

    var res = await fetch(buildAssetsUrl(fallbackHost, config, params));
    if (!res.ok) throw new Error('Failed to fetch assets: ' + res.status);
    return await res.json();
  }

  /* ==========================================================================
     2. getProducts client
     Liquid cannot resolve arbitrary Shopify product ids client-side, so the
     product cards for a Tolstoy-driven slide have to be looked up through
     Tolstoy — `asset.productIds` is the only linkage we get. Same host
     behavior as getAssets, but here the appKey travels in the query string:
     this endpoint is not cached by path.
     ========================================================================== */

  /**
   * @param {string} base
   * @param {TolstoyConfig} config
   * @param {string[]} externalProductIds
   * @returns {string}
   */
  function buildProductsUrl(base, config, externalProductIds) {
    var query = new URLSearchParams({
      appKey: config.appKey,
      appUrl: config.appUrl,
      widgetId: config.widgetId,
      externalProductIds: JSON.stringify(externalProductIds)
    });

    return base + '/ai-widgets/v1/media/getProducts?' + query;
  }

  /**
   * Fetches one batch (<= MAX_IDS_PER_REQUEST ids), primary host then fallback.
   * @param {TolstoyConfig} config
   * @param {string[]} ids
   * @returns {Promise<TolstoyProduct[]>}
   */
  async function fetchProductBatch(config, ids) {
    var primary = config.apiBase || PRIMARY_API_BASE;
    var fallbackHost = config.apiBaseFallback || FALLBACK_API_BASE;

    try {
      var first = await fetch(buildProductsUrl(primary, config, ids));
      if (first.ok) {
        /** @type {FetchProductsResult} */
        var firstBody = await first.json();
        return firstBody.products || [];
      }
    } catch (error) {
      // Network error on the primary host — fall through to the fallback.
    }

    var res = await fetch(buildProductsUrl(fallbackHost, config, ids));
    if (!res.ok) throw new Error('Failed to fetch products: ' + res.status);
    /** @type {FetchProductsResult} */
    var body = await res.json();
    return body.products || [];
  }

  /**
   * Resolves Shopify product ids to Tolstoy product records.
   *
   * Ids are de-duplicated and split into batches of MAX_IDS_PER_REQUEST. A batch
   * that fails is dropped silently (its cards simply do not render) rather than
   * failing the whole lookup — same contract as the reference implementation.
   *
   * @param {TolstoyConfig} config
   * @param {string[]} externalProductIds
   * @returns {Promise<TolstoyProduct[]>}
   */
  async function fetchProducts(config, externalProductIds) {
    /** @type {string[]} */
    var unique = [];
    /** @type {Record<string, true>} */
    var seen = {};

    externalProductIds.forEach(function (id) {
      var key = String(id == null ? '' : id).trim();
      if (!key || seen[key]) return;
      seen[key] = true;
      unique.push(key);
    });

    if (unique.length === 0) return [];

    /** @type {string[][]} */
    var batches = [];
    for (var i = 0; i < unique.length; i += MAX_IDS_PER_REQUEST) {
      batches.push(unique.slice(i, i + MAX_IDS_PER_REQUEST));
    }

    var results = await Promise.all(
      batches.map(function (batch) {
        return fetchProductBatch(config, batch).catch(function () {
          return /** @type {TolstoyProduct[]} */ ([]);
        });
      })
    );

    return results.reduce(function (all, batch) {
      return all.concat(batch);
    }, /** @type {TolstoyProduct[]} */ ([]));
  }

  /* ==========================================================================
     3. Media / rendition selection
     Each surface asks for the rendition that fits its box (row tile 960 /
     preview video, pop-up slide 1280 / player video). Playback uses native HLS
     only where it actually works (Safari/iOS) with a progressive MP4 cascade
     (1280 -> 960 -> 640) everywhere else — a bare <video src="….m3u8">
     silently fails outside Safari.

     Every selector returns a string or null: `ai-widgets/v1` is undocumented
     and nothing about it is guaranteed, so a missing rendition must end up as
     "skip this asset", never as src="undefined".
     ========================================================================== */

  /**
   * Detects native HLS support (Safari desktop + iOS).
   *
   * `canPlayType('application/vnd.apple.mpegurl')` is NOT a sufficient signal
   * on its own: Chrome on macOS answers 'maybe' (verified) and then fails to
   * play the manifest, which would break every video outside Safari. So the
   * user agent decides and canPlayType only acts as a second gate.
   *
   * @returns {boolean}
   */
  function supportsNativeHls() {
    if (typeof navigator === 'undefined' || typeof document === 'undefined') return false;

    // Safari (desktop + iOS) but not Chrome/Chromium/Android or the iOS builds
    // of Chrome/Firefox, all of which carry "Safari" in the UA too.
    var isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);
    if (!isSafari) return false;

    return document.createElement('video').canPlayType('application/vnd.apple.mpegurl') !== '';
  }

  /**
   * Picks the first usable URL from a size ladder. Anything that is not a
   * non-empty string is treated as absent, whatever the response actually held.
   *
   * @param {...(string|null|undefined)} candidates
   * @returns {string|null}
   */
  function firstUrl(candidates) {
    for (var i = 0; i < arguments.length; i += 1) {
      var candidate = arguments[i];
      if (typeof candidate === 'string' && candidate !== '') return candidate;
    }
    return null;
  }

  /**
   * Display rendition of an image asset, sized to the surface it lands on: the
   * row tiles render at ~25vw desktop / ~62vw mobile (960 is the right ask) and
   * a pop-up slide fills a whole media pane (1280). The 250px rendition the
   * reference uses for its small circle tiles is far too soft for either. Walks
   * down the size ladder so a partially populated `sources` object still yields
   * a URL.
   *
   * @param {ImageAsset} asset
   * @param {keyof ImageSources} [size]
   * @returns {string|null}
   */
  function imageSourceUrl(asset, size) {
    var sources = asset.sources || /** @type {ImageSources} */ ({});
    var wanted = size || '1280';
    return firstUrl(sources[wanted], sources['1280'], sources['960'], sources['480'], sources['250']);
  }

  /**
   * The URL the *player* should load for a video asset — pop-up slides only.
   * Native-HLS browsers get the HLS manifest when present; everything else gets
   * the best available progressive MP4 (1280 -> 960 -> 640).
   *
   * @param {VideoAsset} asset
   * @returns {string|null}
   */
  function videoSource(asset) {
    var sources = asset.sources || /** @type {VideoSources} */ ({});
    if (sources.hls && supportsNativeHls()) return sources.hls;
    return firstUrl(sources['1280'], sources['960'], sources['640'], sources['480']);
  }

  /**
   * The URL an in-tile muted preview should load (`sources['640']` then
   * `['480']`). Row tiles are ~25vw desktop / ~62vw mobile and never go
   * fullscreen, so they must not pull the 1280 player rendition — up to 24 of
   * them can be on one PDP. HLS is deliberately not used here: a preview wants
   * a single small progressive file, and the manifest only plays in Safari.
   *
   * @param {VideoAsset} asset
   * @returns {string|null}
   */
  function previewVideoSource(asset) {
    var sources = asset.sources || /** @type {VideoSources} */ ({});
    return firstUrl(sources['640'], sources['480'], sources['960'], sources['1280']);
  }

  /**
   * Poster for the video element. Falls back down the size ladder when the
   * requested rendition is absent (1280/1920 are optional).
   *
   * @param {VideoAsset} asset
   * @param {keyof VideoPoster} [size]
   * @returns {string|null}
   */
  function posterUrl(asset, size) {
    var poster = asset.poster || /** @type {VideoPoster} */ ({});
    var wanted = size || '480';
    return firstUrl(poster[wanted], poster['960'], poster['480'], poster['250']);
  }

  /**
   * Captions passthrough — nullable WebVTT URL.
   * @param {VideoAsset} asset
   * @returns {string|null}
   */
  function captionsUrl(asset) {
    return asset.captionsUrl;
  }

  /* ==========================================================================
     4. Section behavior
     Ported from the inline <script> that used to live in as-seen-on-v2.liquid.
     Element lookups and listeners are grouped in bind() so they can be re-run
     after Tolstoy replaces the section's content. The document-level keydown
     listener is attached once per section and reads the current elements
     through the closure, so rebinding never doubles it.
     ========================================================================== */

  /* ------------------------------------------------------------- helpers --- */

  /**
   * @param {HTMLElement} root
   * @returns {SectionConfig}
   */
  function readConfig(root) {
    var data = root.dataset;

    return {
      appKey: (data.appKey || '').trim(),
      widgetId: (data.widgetId || '').trim(),
      settingsId: (data.settingsId || '').trim(),
      appUrl: (data.appUrl || '').trim(),
      productId: (data.productId || '').trim(),
      tags: (data.tags || '')
        .split(',')
        .map(function (tag) {
          return tag.trim();
        })
        .filter(function (tag) {
          return tag !== '';
        }),
      maxItems: parseMaxItems(data.maxItems),
      currency: (data.currency || '').trim().toUpperCase()
    };
  }

  /**
   * @param {string|undefined} raw
   * @returns {number}
   */
  function parseMaxItems(raw) {
    var value = parseInt(raw || '', 10);
    if (!isFinite(value) || value < 1) return DEFAULT_MAX_ITEMS;
    return Math.min(value, PAGE_SIZE);
  }

  /** @returns {boolean} */
  function isDesignMode() {
    var shopify = /** @type {AsoWindow} */ (window).Shopify;
    return Boolean(shopify && shopify.designMode);
  }

  /**
   * The creator handle for the chip, the pop-up bar and the tile's accessible
   * name — `creatorProfile.handle`, never `asset.name`.
   *
   * Real data lesson: `name` is the upload filename
   * ("VENROYES26_D1_4x5_EDEN_1_NO_LOGO_4.jpg") and `creatorProfile` is null on
   * every asset of the first configured product. With no handle we render no
   * chip at all — a .jpg stamped across the row is worse than nothing.
   *
   * @param {Asset} asset
   * @returns {string}
   */
  function creatorHandle(asset) {
    var profile = asset.creatorProfile;
    if (!profile || typeof profile.handle !== 'string') return '';

    var handle = profile.handle.trim();
    if (!handle) return '';

    /* Tolstoy stores the handle bare ("venroy"), the design shows it prefixed
       ("@melitasuicaaaa") — add the @ only when it is missing. */
    return handle.charAt(0) === '@' ? handle : '@' + handle;
  }

  /**
   * Only http(s) URLs may reach the DOM. `productUrl` comes from an
   * unauthenticated third-party API, and a `javascript:` value on an <a href>
   * executes on click.
   *
   * @param {string|null|undefined} url
   * @returns {string|null}
   */
  function safeUrl(url) {
    if (!url) return null;
    try {
      var parsed = new URL(url, window.location.href);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * @param {EventTarget} target
   * @param {string} type
   * @param {EventListener} handler
   * @param {AddEventListenerOptions} [options]
   * @returns {Teardown}
   */
  function listen(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    return function () {
      target.removeEventListener(type, handler, options);
    };
  }

  /**
   * Un-lazies every image in a subtree so it starts loading even while hidden.
   * @param {Element|undefined} container
   * @returns {void}
   */
  function revealImages(container) {
    if (!container) return;
    var images = /** @type {NodeListOf<HTMLImageElement>} */ (
      container.querySelectorAll('img[loading="lazy"]')
    );
    images.forEach(function (image) {
      image.loading = 'eager';
    });
  }

  /**
   * Safari (and older browsers) return undefined from play(); newer ones a promise.
   * @param {HTMLVideoElement} video
   * @returns {void}
   */
  function safePlay(video) {
    var playing = video.play();
    if (playing && typeof playing.catch === 'function') playing.catch(function () {});
  }

  /** @returns {boolean} */
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------------------------------------------------------- controller --- */

  /**
   * @param {HTMLElement} root
   * @returns {SectionController}
   */
  function createController(root) {
    /** @type {HTMLElement|null} */
    var modal = null;
    /** The pop-up's Swiper container — the media pane. @type {HTMLElement|null} */
    var mediapane = null;
    /** The pop-up's Swiper instance, one per section root. @type {any} */
    var modalSwiper = null;
    /** @type {HTMLElement[]} */
    var slides = [];
    /** @type {HTMLElement[]} */
    var sets = [];
    /** @type {HTMLElement[]} */
    var dots = [];
    /** @type {HTMLElement|null} */
    var barHandle = null;
    /** @type {HTMLElement|null} */
    var closeBtn = null;
    /** @type {HTMLElement|null} */
    var lastFocused = null;
    var index = 0;
    var destroyed = false;

    /**
     * Element listeners — dropped and re-created on every bind().
     * @type {Teardown[]}
     */
    var bindings = [];

    /**
     * Starts loading the images of one slide (and its product cards).
     *
     * The pop-up is `display: none` until it opens, so a `loading="lazy"` image
     * inside it is never fetched — the first open showed a blank media pane and
     * grey card images until the bytes arrived. Flipping the attribute to
     * `eager` un-lazies the element and starts the request immediately; nothing
     * else on the page is touched, so the other slides stay lazy.
     *
     * @param {number} i
     * @returns {void}
     */
    function warmSlide(i) {
      if (!slides.length) return;
      var at = (i + slides.length) % slides.length;
      revealImages(slides[at]);
      revealImages(sets[at]);
    }

    /**
     * The single entry point for "show asset N" — every navigation route (tile
     * click, dot, arrow key, drag, wheel) ends up here, which is what keeps the
     * dots, the product panel, the bar handle and video play/pause in sync.
     *
     * The index CLAMPS, it does not wrap: a non-looping Swiper stops at the last
     * slide, so wrapping here would make the arrow keys disagree with a drag
     * (VEN-241). `loop: true` is deliberately not an option — its slide clones
     * would break the data-index <-> dot <-> product-set mapping.
     *
     * @param {number} i
     * @param {boolean} [fromSlider] the Swiper moved itself; do not move it back
     * @returns {void}
     */
    function showSlide(i, fromSlider) {
      if (!slides.length) return;
      index = Math.min(Math.max(i, 0), slides.length - 1);

      /* only once the pop-up is actually open (bind() calls showSlide(0) on page
         load, and a PDP should not pay for media nobody asked for): the active
         slide first, then its neighbours, so arrowing/swiping on never waits for
         a request that could have started already */
      if (modal && modal.hasAttribute('open')) {
        warmSlide(index);
        warmSlide(index + 1);
        warmSlide(index - 1);
      }

      /* keep the slider on the same slide — unless it is the one that moved.
         `runCallbacks: false` stops slideChange bouncing straight back in here. */
      if (!fromSlider && modalSwiper) {
        /* undefined falls through to the instance's own `speed` */
        var speed = prefersReducedMotion() ? 0 : undefined;
        modalSwiper.slideTo(index, speed, false);
      }

      slides.forEach(function (slide, n) {
        var isActive = n === index;
        slide.toggleAttribute('data-active', isActive);

        var video = slide.querySelector('video');
        if (!video) return;
        if (isActive) {
          /* bind() calls showSlide(0) while the pop-up is still closed — captions
             are only worth fetching once the shopper actually sees the slide */
          if (modal && modal.hasAttribute('open')) loadCaptions(video);
          safePlay(video);
        } else {
          video.pause();
        }
      });

      sets.forEach(function (set, n) {
        set.toggleAttribute('data-active', n === index);
      });

      dots.forEach(function (dot, n) {
        dot.setAttribute('aria-current', String(n === index));
      });

      if (barHandle) barHandle.textContent = slides[index].dataset.handle || '';
    }

    /**
     * @param {number} i
     * @returns {void}
     */
    function openModal(i) {
      if (!modal) return;
      lastFocused = /** @type {HTMLElement|null} */ (document.activeElement);
      modal.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
      showSlide(i);

      /* the dialog was display:none when the Swiper was built, so it measured
         zero and every drag would be dead — re-measure it once layout has
         settled, then jump (not animate) to the asset that was clicked */
      window.requestAnimationFrame(function () {
        if (!modalSwiper) return;
        modalSwiper.update();
        modalSwiper.slideTo(index, 0, false);
      });

      if (closeBtn) closeBtn.focus();
    }

    /** @returns {void} */
    function closeModal() {
      if (!modal) return;
      modal.removeAttribute('open');
      document.body.style.overflow = '';
      modal.querySelectorAll('video').forEach(function (video) {
        video.pause();
        video.currentTime = 0;
      });
      if (lastFocused) lastFocused.focus();
    }

    /**
     * @param {Event} event
     * @returns {void}
     */
    function onDocumentKeydown(event) {
      if (!modal || !modal.hasAttribute('open')) return;
      var keyEvent = /** @type {KeyboardEvent} */ (event);
      var key = keyEvent.key;

      if (key === 'Escape') {
        closeModal();
        return;
      }
      if (key === 'ArrowRight') {
        showSlide(index + 1);
        return;
      }
      if (key === 'ArrowLeft') {
        showSlide(index - 1);
        return;
      }

      if (key === 'Tab') {
        var items = /** @type {HTMLElement[]} */ (
          Array.prototype.slice.call(modal.querySelectorAll(FOCUSABLE)).filter(function (el) {
            return el.offsetParent !== null;
          })
        );
        if (!items.length) return;

        var first = items[0];
        var last = items[items.length - 1];

        if (keyEvent.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!keyEvent.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    /**
     * Turns the pop-up's media pane into a Swiper — the same library every other
     * carousel in this theme uses. VEN-241: the pane used to be two different
     * mechanisms, a desktop cross-fade and a mobile scroll-snap track, and
     * neither could be dragged on desktop. It is now one horizontal slider at
     * every width, so drag, wheel, dots and the arrow keys all agree.
     *
     * The pane is the container and `.aso-modal__track` its `.swiper-wrapper`,
     * which leaves the dots as untransformed absolute siblings — the standard
     * Swiper pagination arrangement.
     *
     * Two things this must keep doing:
     *   - resolve the element from `root`, never a document-wide selector: a page
     *     can carry more than one "As Seen On" section;
     *   - destroy any previous instance first, so a Tolstoy re-render and a
     *     theme-editor section reload cannot leave orphaned drag handlers.
     *
     * @returns {void}
     */
    function syncModalSlider() {
      var el = /** @type {(HTMLElement & {swiper?: any})|null} */ (mediapane);
      modalSwiper = null;
      if (!el) return;

      if (el.swiper) el.swiper.destroy(true, true);

      /* the Liquid shell is empty until Tolstoy answers — bind() runs again on
         the rendered slides, and dots/keys stay functional without an instance */
      if (!slides.length) return;

      var Swiper = /** @type {any} */ (window).Swiper;
      if (typeof Swiper !== 'function') return; // no slider: dots and keys still work

      /* markup out of step with this script — a half-synced dev server, a partial
         deploy — leaves the pane without its wrapper. Swiper would build with an
         empty $wrapperEl and throw from slideTo, taking the whole pop-up with it,
         so degrade to dots and keys the same way a missing library does */
      if (!el.querySelector('.swiper-wrapper')) return;

      modalSwiper = new Swiper(el, {
        slidesPerView: 1,
        grabCursor: true,
        speed: 400,
        /* forceToAxis so a vertical wheel still scrolls the mobile dialog, and
           deliberately NO releaseOnEdges: an open pop-up locks the page scroll
           (see openModal), so handing the wheel back at the ends would look
           broken rather than helpful */
        mousewheel: { forceToAxis: true, sensitivity: 1 },
        on: {
          slideChange: function () {
            if (modalSwiper) showSlide(modalSwiper.activeIndex, true);
          }
        }
      });
    }

    /** @type {Teardown[]} */
    var documentBindings = [listen(document, 'keydown', onDocumentKeydown)];

    /** @returns {void} */
    function unbindElements() {
      bindings.forEach(function (teardown) {
        teardown();
      });
      bindings = [];
    }

    /** @returns {void} */
    function bind() {
      unbindElements();

      modal = /** @type {HTMLElement|null} */ (root.querySelector('[data-aso-modal]'));
      if (!modal) return;

      var dialog = modal;

      mediapane = /** @type {HTMLElement|null} */ (dialog.querySelector('.aso-modal__mediapane'));
      slides = /** @type {HTMLElement[]} */ (
        Array.prototype.slice.call(dialog.querySelectorAll('.aso-modal__slide'))
      );
      sets = /** @type {HTMLElement[]} */ (
        Array.prototype.slice.call(dialog.querySelectorAll('.aso-modal__set'))
      );
      dots = /** @type {HTMLElement[]} */ (
        Array.prototype.slice.call(dialog.querySelectorAll('[data-aso-dot]'))
      );
      barHandle = /** @type {HTMLElement|null} */ (dialog.querySelector('[data-aso-bar-handle]'));
      closeBtn = /** @type {HTMLElement|null} */ (dialog.querySelector('[data-aso-close]'));
      index = 0;

      var tiles = /** @type {NodeListOf<HTMLElement>} */ (root.querySelectorAll('[data-aso-open]'));
      tiles.forEach(function (tile) {
        bindings.push(
          listen(tile, 'click', function () {
            openModal(Number(tile.dataset.index) || 0);
          })
        );

        /* a click is imminent — give the slide's media a head start, the same
           trick the reference preloader uses */
        bindings.push(
          listen(
            tile,
            'pointerdown',
            function () {
              warmSlide(Number(tile.dataset.index) || 0);
            },
            { passive: true }
          )
        );
      });

      dots.forEach(function (dot) {
        bindings.push(
          listen(dot, 'click', function () {
            showSlide(Number(dot.dataset.index) || 0);
          })
        );
      });

      if (closeBtn) bindings.push(listen(closeBtn, 'click', closeModal));

      bindings.push(
        listen(dialog, 'click', function (event) {
          if (event.target === dialog) closeModal();
        })
      );

      /* tile play/pause — must not open the modal */
      var controls = /** @type {NodeListOf<HTMLElement>} */ (
        root.querySelectorAll('[data-aso-playpause]')
      );
      controls.forEach(function (control) {
        /**
         * @param {Event} event
         * @returns {void}
         */
        function toggle(event) {
          event.preventDefault();
          event.stopPropagation();

          var tile = control.closest('.aso__tile');
          var video = tile ? tile.querySelector('video') : null;
          var wasPlaying = control.getAttribute('aria-pressed') === 'true';

          control.setAttribute('aria-pressed', String(!wasPlaying));
          control.setAttribute('aria-label', wasPlaying ? 'Play' : 'Pause');

          var label = control.querySelector('[data-aso-pplabel]');
          if (label) label.textContent = wasPlaying ? 'Play' : 'Pause';

          if (!video) return;
          if (wasPlaying) {
            video.pause();
          } else {
            safePlay(video);
          }
        }

        bindings.push(listen(control, 'click', toggle));
        bindings.push(
          listen(control, 'keydown', function (event) {
            var key = /** @type {KeyboardEvent} */ (event).key;
            if (key === 'Enter' || key === ' ') toggle(event);
          })
        );
      });

      syncModalSlider();
      showSlide(0);
    }

    /** @returns {void} */
    function destroy() {
      destroyed = true;
      unbindElements();
      if (modalSwiper) {
        modalSwiper.destroy(true, true);
        modalSwiper = null;
      }
      documentBindings.forEach(function (teardown) {
        teardown();
      });
      documentBindings.length = 0;
      /* a section can be torn down (theme editor) while the pop-up is open —
         never leave the page scroll locked */
      if (modal && modal.hasAttribute('open')) document.body.style.overflow = '';
    }

    return {
      bind: bind,
      destroy: destroy,
      isDestroyed: function () {
        return destroyed;
      }
    };
  }

  /* ==========================================================================
     5. Tolstoy rendering — builds the section's row and pop-up markup
     DOM is built with createElement/textContent only, never innerHTML with API
     strings.
     ========================================================================== */

  /**
   * @param {string} className
   * @param {string} path
   * @returns {SVGElement}
   */
  function createIcon(className, path) {
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', className);
    svg.setAttribute('width', '9');
    svg.setAttribute('height', '11');
    svg.setAttribute('viewBox', '0 0 9 11');
    svg.setAttribute('fill', 'currentColor');
    svg.setAttribute('aria-hidden', 'true');

    var shape = document.createElementNS(SVG_NS, 'path');
    shape.setAttribute('d', path);
    svg.appendChild(shape);

    return svg;
  }

  /**
   * The `.aso__playpause` control of a video tile, icons included — the markup
   * assets/as-seen-on-v2.css and the tile handlers select on.
   * @returns {HTMLElement}
   */
  function createPlayPause() {
    var control = document.createElement('span');
    control.className = 'aso__playpause';
    control.setAttribute('role', 'button');
    control.setAttribute('tabindex', '0');
    control.setAttribute('aria-pressed', 'true');
    control.setAttribute('aria-label', 'Pause');
    control.setAttribute('data-aso-playpause', '');

    control.appendChild(createIcon('aso-i-play', 'M0 0l9 5.5L0 11z'));
    control.appendChild(createIcon('aso-i-pause', 'M0 0h3v11H0zM6 0h3v11H6z'));

    var label = document.createElement('span');
    label.setAttribute('data-aso-pplabel', '');
    label.textContent = 'Pause';
    control.appendChild(label);

    return control;
  }

  /**
   * @param {string} src
   * @param {string} alt
   * @param {string} [className]
   * @returns {HTMLImageElement}
   */
  function createImage(src, alt, className) {
    var image = document.createElement('img');
    if (className) image.className = className;
    image.loading = 'lazy';
    image.alt = alt;
    image.src = src;
    return image;
  }

  /**
   * Loads a slide's captions the first time that slide is opened.
   *
   * Lazy on purpose: the URL is parked on the element at render time and only
   * fetched here, so a pop-up that is never opened costs zero VTT requests (up
   * to 24 assets can be on one PDP).
   *
   * A <track src> pointing at another origin is only honoured when the media
   * element itself opts into CORS, and switching the <video> to crossorigin
   * would break playback wherever the media response lacks matching headers. So
   * we fetch the VTT ourselves and hand the element a same-origin blob: URL.
   *
   * The track is shown by default: these videos are muted and have no controls,
   * so there is no affordance for the shopper to turn captions on.
   *
   * Fire-and-forget: any failure simply leaves the video without captions, with
   * no console noise and no effect on playback. The object URLs are
   * deliberately never revoked — a handful of small VTT blobs living for the
   * page's lifetime is cheaper than tracking when each <video> goes away.
   *
   * @param {HTMLVideoElement} video
   * @returns {void}
   */
  function loadCaptions(video) {
    var url = video.getAttribute(CAPTIONS_ATTRIBUTE);
    if (!url) return;
    video.removeAttribute(CAPTIONS_ATTRIBUTE); // once per element

    fetch(url)
      .then(function (res) {
        return res.ok ? res.text() : Promise.reject();
      })
      .then(function (vtt) {
        var track = document.createElement('track');
        track.kind = 'captions';
        track.label = 'Captions';
        track.default = true;
        track.src = URL.createObjectURL(new Blob([vtt], { type: 'text/vtt' }));
        video.appendChild(track);
        // `default` only selects a track at load time; this one arrives later
        if (track.track) track.track.mode = 'showing';
      })
      .catch(function () {});
  }

  /**
   * Row tiles autoplay muted; pop-up slides are started by showSlide() so that
   * only the active one plays.
   *
   * The rendition differs per variant: a tile is a decorative preview in a
   * ~25vw/~62vw box and gets the small progressive file, only the pop-up gets
   * the player rendition (and the HLS manifest in Safari). Captions belong to
   * the pop-up too — a tile has no captions UI — and their URL is only parked
   * on the element here, to be fetched when the slide is first opened.
   *
   * Returns null when the asset carries no usable video URL at all.
   *
   * @param {VideoAsset} asset
   * @param {'tile'|'slide'} variant
   * @param {string} [className]
   * @returns {HTMLVideoElement|null}
   */
  function createVideo(asset, variant, className) {
    var src = variant === 'tile' ? previewVideoSource(asset) : videoSource(asset);
    if (!src) return null;

    var video = document.createElement('video');
    if (className) video.className = className;

    /* properties, not just attributes — muted must be set for autoplay to be
       allowed and playsInline keeps iOS from going fullscreen */
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;
    video.autoplay = variant === 'tile';
    /* a closed pop-up should cost nothing; showSlide() starts the active slide */
    if (variant === 'slide') video.preload = 'none';

    /* both boxes are large enough to need the 960 poster: the tile is ~25vw
       desktop / ~62vw mobile, the slide fills the media pane */
    var poster = posterUrl(asset, '960');
    if (poster) video.poster = poster;
    video.src = src;

    var captions = variant === 'slide' ? captionsUrl(asset) : null;
    if (captions) video.setAttribute(CAPTIONS_ATTRIBUTE, captions);

    return video;
  }

  /**
   * `alt` is the creator handle when there is one, otherwise empty: the tile
   * button already carries an accessible name, and `asset.name` is a filename —
   * pure noise in a screen reader.
   *
   * @param {Asset} asset
   * @returns {HTMLElement|null}
   */
  function createTileMedia(asset) {
    if (asset.type === 'video') return createVideo(asset, 'tile', 'aso__media');

    /* 960, not the reference's 250px circle rendition: this tile is ~25vw
       desktop / ~62vw mobile, i.e. a ~700px box on a retina phone */
    var src = imageSourceUrl(asset, '960');
    return src ? createImage(src, creatorHandle(asset), 'aso__media') : null;
  }

  /**
   * @param {Asset} asset
   * @returns {HTMLElement|null}
   */
  function createSlideMedia(asset) {
    if (asset.type === 'video') return createVideo(asset, 'slide');

    var src = imageSourceUrl(asset, '1280');
    return src ? createImage(src, creatorHandle(asset)) : null;
  }

  /**
   * @param {Asset} asset
   * @param {number} position
   * @returns {HTMLButtonElement|null}
   */
  function createTile(asset, position) {
    var media = createTileMedia(asset);
    if (!media) return null;

    var handle = creatorHandle(asset);

    var tile = document.createElement('button');
    tile.type = 'button';
    tile.className = 'aso__tile swiper-slide';
    tile.setAttribute('data-aso-open', '');
    tile.dataset.index = String(position);
    tile.setAttribute('aria-haspopup', 'dialog');
    tile.setAttribute('aria-label', handle || DEFAULT_TILE_LABEL);
    tile.appendChild(media);

    if (handle) {
      var chip = document.createElement('span');
      chip.className = 'aso__handle';
      chip.textContent = handle;
      tile.appendChild(chip);
    }

    if (asset.type === 'video') tile.appendChild(createPlayPause());

    return tile;
  }

  /**
   * @param {Asset} asset
   * @param {number} position
   * @returns {HTMLElement}
   */
  function createSlide(asset, position) {
    var slide = document.createElement('div');
    slide.className = 'aso-modal__slide swiper-slide';
    slide.dataset.index = String(position);
    slide.dataset.handle = creatorHandle(asset); // feeds the pop-up bar; empty when unknown

    var media = createSlideMedia(asset);
    if (media) slide.appendChild(media);

    return slide;
  }

  /**
   * @param {number} position
   * @returns {HTMLButtonElement}
   */
  function createDot(position) {
    var dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'aso-modal__dot';
    dot.setAttribute('data-aso-dot', '');
    dot.dataset.index = String(position);
    dot.setAttribute('aria-current', 'false');
    dot.setAttribute('aria-label', String(position + 1));
    return dot;
  }

  /**
   * Shopify CDN images are resized with `width` — the URL may already have a query.
   * @param {string} url
   * @param {number} width
   * @returns {string}
   */
  function sizedImageUrl(url, width) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /**
   * @param {number} amount
   * @param {number} decimals
   * @param {string} thousands
   * @param {string} decimal
   * @returns {string}
   */
  function withDelimiters(amount, decimals, thousands, decimal) {
    var parts = amount.toFixed(decimals).split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.length > 1 ? whole + decimal + parts[1] : whole;
  }

  /**
   * Formats with the shop's own `money_format`. Returns null when the format is
   * absent or uses a placeholder we do not handle.
   *
   * Currently unused, and nothing supplies it a format any more: VEN-235 moved
   * card prices onto the theme's own `100 AUD` shape, which money_format cannot
   * express, and removed the `data-money-format` attribute that fed this. Kept
   * only so the parsing is not lost if a money_format price is ever wanted
   * again; delete it with MONEY_PLACEHOLDER when that is ruled out.
   *
   * @param {string} format
   * @param {number} amount
   * @returns {string|null}
   */
  function formatWithMoneyFormat(format, amount) {
    // the setting may carry markup, e.g. <span class=money>${{amount}}</span>
    var text = format.replace(/<[^>]*>/g, '').trim();
    var match = text.match(MONEY_PLACEHOLDER);
    if (!match) return null;

    var value;
    switch (match[1]) {
      case 'amount':
        value = withDelimiters(amount, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = withDelimiters(amount, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(amount, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(amount, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(amount, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(amount, 0, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = withDelimiters(amount, 2, ' ', '.');
        break;
      default:
        return null;
    }

    return text.replace(MONEY_PLACEHOLDER, value);
  }

  /**
   * Card price, in the theme's own shape: amount, a space, the ISO code —
   * `100 AUD`. This mirrors snippets/format-price.liquid, which every other
   * price on the storefront renders through (product grid, PDP, cart, account),
   * so a pop-up card reads the same as the page it opens over. The snippet
   * cannot be reused here because these cards are built in JS from the Tolstoy
   * response, long after Liquid has run.
   *
   * Decimals follow `money_without_trailing_zeros`, as the snippet does: a whole
   * amount prints bare (`100 AUD`), a fractional one keeps both places
   * (`99.50 AUD`).
   *
   * `money_format` is deliberately no longer consulted. The theme's format
   * carries no currency symbol and no grouping of its own, so the shop setting
   * has nothing left to contribute — and dropping it drops the hazard that came
   * with it: a currency app (the `glc-money` wrapper in the AU shop's format)
   * rewrites `money_format` per visitor by geo-IP, serving the same page
   * `AUD ${{amount}}` in one session and `{{amount}} ฿` in another.
   *
   * That hazard is also why the code below comes from `product.currencyCode`
   * and not from the section's `data-currency`. Tolstoy's getProducts takes only
   * `appUrl` — no market, no currency context — so `variants[].price` is always
   * in the shop's primary currency with its own `currencyCode` attached, and
   * that code is the only label that truthfully describes this amount. Pairing
   * it with a visitor's converted code would print an AUD number called baht: a
   * wrong price, not a cosmetic mismatch. `data-currency` is the fallback for
   * the case where the API omits the code entirely, and it is safe in that role
   * only because the section renders it from `shop.currency` — the primary
   * currency these amounts are actually in — and never from `cart.currency`,
   * which on a Markets session is the visitor's presentment code.
   *
   * `variants[].price` is already in major units (250, not 25000) — it is not
   * cents, so do not divide by 100.
   *
   * @param {TolstoyProduct} product
   * @param {RenderOptions} options
   * @returns {string}
   */
  function formatPrice(product, options) {
    var variants = product.variants || [];
    var amount = Number.NaN;

    variants.forEach(function (variant) {
      var value =
        typeof variant.price === 'number' ? variant.price : parseFloat(String(variant.price));
      if (!isFinite(value)) return;
      if (!isFinite(amount) || value < amount) amount = value;
    });

    if (!isFinite(amount)) return '';

    var value = withDelimiters(amount, amount % 1 === 0 ? 0 : 2, ',', '.');
    var code = (product.currencyCode || '').trim().toUpperCase() || options.currency;

    return code ? value + ' ' + code : value;
  }

  /**
   * Where "Shop Now" points.
   *
   * `productUrl` comes back on the myshopify domain
   * (https://venroystore.myshopify.com/products/…) while the storefront runs on
   * venroy.com.au — following it costs a redirect hop, shows the wrong domain on
   * hover and risks losing market/locale context. The response also carries the
   * handle, so build an origin-relative link and only fall back to the
   * (validated) absolute URL when it is missing.
   *
   * @param {TolstoyProduct} product
   * @returns {string|null}
   */
  function productHref(product) {
    var handle = (product.handle || '').trim();
    if (handle) return '/products/' + encodeURIComponent(handle);
    return safeUrl(product.productUrl);
  }

  /**
   * @param {TolstoyProduct} product
   * @param {RenderOptions} options
   * @returns {HTMLElement}
   */
  function createCard(product, options) {
    var card = document.createElement('div');
    card.className = 'aso-card';

    var title = product.title || '';

    if (product.imageUrl) {
      card.appendChild(
        createImage(sizedImageUrl(product.imageUrl, PRODUCT_IMAGE_WIDTH), title, 'aso-card__img')
      );
    } else {
      var placeholder = document.createElement('div');
      placeholder.className = 'aso-card__img';
      card.appendChild(placeholder);
    }

    var titleEl = document.createElement('div');
    titleEl.className = 'aso-card__title';
    titleEl.textContent = title;
    card.appendChild(titleEl);

    var priceEl = document.createElement('div');
    priceEl.className = 'aso-card__price';
    priceEl.textContent = formatPrice(product, options);
    card.appendChild(priceEl);

    var href = productHref(product);
    if (href) {
      var cta = document.createElement('a');
      cta.className = 'aso-card__cta';
      cta.href = href;
      cta.textContent = 'Shop Now';
      /* four identical "Shop Now" links in one set are indistinguishable to a
         screen reader without the product name */
      if (title) cta.setAttribute('aria-label', 'Shop Now: ' + title);
      card.appendChild(cta);
    }

    return card;
  }

  /**
   * The ids this asset should render cards for, capped at MAX_CARDS_PER_SET.
   * @param {Asset} asset
   * @returns {string[]}
   */
  function assetProductIds(asset) {
    return (asset.productIds || [])
      .map(function (id) {
        return String(id == null ? '' : id).trim();
      })
      .filter(function (id) {
        return id !== '';
      })
      .slice(0, MAX_CARDS_PER_SET);
  }

  /**
   * @param {Asset} asset
   * @param {number} position
   * @param {Record<string, TolstoyProduct>} products
   * @param {RenderOptions} options
   * @returns {HTMLElement}
   */
  function createProductSet(asset, position, products, options) {
    var set = document.createElement('div');
    set.className = 'aso-modal__set';
    set.dataset.index = String(position);

    assetProductIds(asset).forEach(function (id) {
      var product = products[id];
      if (product) set.appendChild(createCard(product, options));
    });

    return set;
  }

  /**
   * @param {Element} container
   * @param {DocumentFragment} content
   * @returns {void}
   */
  function replaceContent(container, content) {
    container.textContent = '';
    container.appendChild(content);
  }

  /**
   * Fills the empty row and pop-up shells with the Tolstoy content.
   *
   * Everything is built into detached fragments first and only then swapped in,
   * so a failure while building can never leave a half-rendered section.
   * Returns false when the expected containers are missing — the caller then
   * leaves the existing DOM alone.
   *
   * @param {HTMLElement} root
   * @param {Asset[]} assets
   * @param {Record<string, TolstoyProduct>} products
   * @param {RenderOptions} options
   * @returns {boolean}
   */
  function renderAssets(root, assets, products, options) {
    var row = root.querySelector('[data-aso-row-track]');
    var mediapane = root.querySelector('.aso-modal__mediapane');
    var track = root.querySelector('[data-aso-track]');
    var productsPane = root.querySelector('.aso-modal__products');
    if (!row || !mediapane || !track || !productsPane) return false;

    var tiles = document.createDocumentFragment();
    var slides = document.createDocumentFragment();
    var sets = document.createDocumentFragment();
    var dots = document.createDocumentFragment();

    assets.forEach(function (asset, position) {
      var tile = createTile(asset, position);
      if (!tile) return; // filtered upstream, but never append a broken element
      tiles.appendChild(tile);
      slides.appendChild(createSlide(asset, position));
      sets.appendChild(createProductSet(asset, position, products, options));
      if (assets.length > 1) dots.appendChild(createDot(position));
    });

    replaceContent(row, tiles);
    replaceContent(track, slides);
    replaceContent(productsPane, sets);

    /* dots only earn their place with more than one item — create or remove
       the container as the asset count needs */
    var dotsContainer = mediapane.querySelector('.aso-modal__dots');
    if (assets.length > 1) {
      if (!dotsContainer) {
        dotsContainer = document.createElement('div');
        dotsContainer.className = 'aso-modal__dots';
        mediapane.appendChild(dotsContainer);
      }
      replaceContent(dotsContainer, dots);
    } else if (dotsContainer) {
      dotsContainer.remove();
    }

    return true;
  }

  /**
   * Resolves every asset's product ids in one de-duplicated lookup.
   * Never rejects: a failed lookup simply means no cards.
   *
   * @param {TolstoyConfig} config
   * @param {Asset[]} assets
   * @returns {Promise<Record<string, TolstoyProduct>>}
   */
  async function loadProducts(config, assets) {
    /** @type {string[]} */
    var ids = [];
    assets.forEach(function (asset) {
      assetProductIds(asset).forEach(function (id) {
        ids.push(id);
      });
    });

    /** @type {Record<string, TolstoyProduct>} */
    var map = {};
    if (ids.length === 0) return map;

    /** @type {TolstoyProduct[]} */
    var products = [];
    try {
      products = await fetchProducts(config, ids);
    } catch (error) {
      return map;
    }

    products.forEach(function (product) {
      var id = String(product.externalProductId == null ? '' : product.externalProductId).trim();
      if (!id) return;
      map[id] = product;
      /* ids come back either bare or as Shopify GIDs — index both so the
         lookup against asset.productIds matches either shape */
      var bare = id.slice(id.lastIndexOf('/') + 1);
      if (bare) map[bare] = product;
    });

    return map;
  }

  /* ==========================================================================
     6. Life cycle
     ========================================================================== */

  /**
   * No Tolstoy media for this product → hide the whole section, heading
   * included (team precedent VEN-211/VEN-181).
   *
   * Exception: in the theme editor the section must stay visible, otherwise a
   * merchant who has just pasted their credentials watches it vanish with no
   * way to select it again — they get an editor hint instead.
   *
   * @param {HTMLElement} root
   * @returns {void}
   */
  function fallback(root) {
    if (isDesignMode()) {
      showEditorHint(root);
      return;
    }

    var wrapper = /** @type {HTMLElement|null} */ (root.closest('.shopify-section')) || root;
    wrapper.style.display = 'none';
  }

  /**
   * @param {HTMLElement} root
   * @returns {void}
   */
  function showEditorHint(root) {
    if (root.querySelector('.aso__empty')) return;

    var hint = document.createElement('p');
    hint.className = 'aso__empty';
    hint.textContent =
      'No Tolstoy media for this product yet. This section is hidden on the live storefront until Tolstoy returns media for it.';
    root.appendChild(hint);
  }

  /**
   * Guards against a response that does not match the documented shape —
   * nothing about `ai-widgets/v1` is contractually guaranteed. An asset of an
   * unknown type, or one whose renditions are all missing, is dropped rather
   * than rendered as a broken element.
   *
   * @param {Asset} asset
   * @returns {boolean}
   */
  function isRenderableAsset(asset) {
    if (!asset) return false;
    if (asset.type === 'video') {
      return previewVideoSource(asset) !== null && videoSource(asset) !== null;
    }
    if (asset.type === 'image') return imageSourceUrl(asset, '960') !== null;
    return false;
  }

  /**
   * Turns the row into a Swiper, matching the other PDP carousels (worn-with
   * and product-recommendations): more than four tiles scroll horizontally
   * instead of wrapping onto a second row. `slidesPerView: 'auto'` so the CSS
   * widths decide, plus free-mode dragging and the same wheel handling
   * "Recommended for you" has — a trackpad's two-finger horizontal swipe is a
   * `wheel` event, and without the mousewheel module the row simply ignored it
   * (VEN-241 item 1). Swiper is global; layout/theme.liquid loads slider.js on
   * every page.
   *
   * Idempotent: an existing instance is destroyed first, so it survives both
   * the Tolstoy re-render and a theme-editor section reload.
   *
   * @param {HTMLElement} root
   * @returns {void}
   */
  function syncRowSlider(root) {
    var el = /** @type {HTMLElement & { swiper?: any }} */ (root.querySelector('.aso__row'));
    if (!el) return;

    if (el.swiper) el.swiper.destroy(true, true);

    var Swiper = /** @type {any} */ (window).Swiper;
    if (typeof Swiper !== 'function') return; // no slider: the row still scrolls via CSS

    new Swiper(el, {
      slidesPerView: 'auto',
      /* the boolean form: this is Swiper 6, which reads the freeModeMomentum*
         params flat — the nested object product-recommendations passes is only
         ever truthy there, its keys are ignored */
      freeMode: true,
      /* forceToAxis keeps a vertical scroll on the page instead of the row, and
         releaseOnEdges hands scrolling back once the row runs out. sensitivity
         above 1 because at the default the row crawls behind the trackpad —
         Recommended for you is matched in gesture, not in pace (VEN-241) */
      mousewheel: { forceToAxis: true, sensitivity: 2, releaseOnEdges: true },
      speed: 400
    });
  }

  /**
   * @param {HTMLElement} root
   * @param {SectionConfig} config
   * @param {SectionController} controller
   * @returns {Promise<void>}
   */
  async function renderFromTolstoy(root, config, controller) {
    /** @type {TolstoyConfig} */
    var tolstoy = {
      appKey: config.appKey,
      widgetId: config.widgetId,
      appUrl: config.appUrl
    };

    /** @type {Asset[]} */
    var assets = [];

    try {
      var result = await fetchAssets(tolstoy, {
        contextProductId: config.productId || undefined,
        tags: config.tags,
        settingsId: config.settingsId || undefined,
        size: PAGE_SIZE
      });
      assets = result && Array.isArray(result.assets) ? result.assets : [];
    } catch (error) {
      /* the section may have been torn down while the request was in flight */
      if (controller.isDestroyed()) return;
      console.warn('[as-seen-on] Tolstoy media request failed', error);
      fallback(root);
      return;
    }

    if (controller.isDestroyed()) return;

    /* the row is a 4-column grid, not a list — render at most what the section's
       "Maximum items" setting allows, after dropping anything unusable */
    var renderable = assets.filter(isRenderableAsset).slice(0, config.maxItems);

    if (renderable.length === 0) {
      fallback(root);
      return;
    }

    var products = await loadProducts(tolstoy, renderable);
    if (controller.isDestroyed()) return;

    var rendered = false;
    try {
      rendered = renderAssets(root, renderable, products, {
        currency: config.currency
      });
    } catch (error) {
      console.warn('[as-seen-on] Tolstoy render failed', error);
    }

    if (!rendered) {
      fallback(root);
      return;
    }

    /* the tiles, slides, dots and product sets are brand new nodes — rebind */
    controller.bind();
    syncRowSlider(root);
  }

  /** @type {Map<HTMLElement, SectionController>} */
  var controllers = new Map();

  /**
   * @param {HTMLElement} root
   * @returns {void}
   */
  function initRoot(root) {
    if (controllers.has(root)) return;

    var controller = createController(root);
    controllers.set(root, controller);

    var config = readConfig(root);

    controller.bind();
    syncRowSlider(root);

    /* safety net: without credentials the Liquid renders no shell at all, so
       there is nothing to fetch for */
    if (!config.appKey || !config.widgetId) return;

    renderFromTolstoy(root, config, controller);
  }

  /**
   * @param {ParentNode} container
   * @returns {void}
   */
  function initIn(container) {
    var roots = /** @type {NodeListOf<HTMLElement>} */ (container.querySelectorAll(ROOT_SELECTOR));
    roots.forEach(initRoot);
  }

  /**
   * @param {Node} container
   * @returns {void}
   */
  function destroyIn(container) {
    controllers.forEach(function (controller, root) {
      if (container.contains(root) || !root.isConnected) {
        controller.destroy();
        controllers.delete(root);
      }
    });
  }

  /** @returns {void} */
  function init() {
    initIn(document);

    /* the theme editor re-renders sections with innerHTML, which neither runs
       this script again nor cleans up the listeners of the old nodes */
    document.addEventListener('shopify:section:load', function (event) {
      var target = /** @type {HTMLElement|null} */ (event.target);
      if (!target || !target.querySelector) return;
      destroyIn(target);
      initIn(target);
    });

    document.addEventListener('shopify:section:unload', function (event) {
      var target = /** @type {HTMLElement|null} */ (event.target);
      if (!target || !target.querySelector) return;
      destroyIn(target);
    });
  }

  /*
   * The section prints its own <script> tag, so a page with N "As Seen On"
   * sections loads this file once but EXECUTES it N times — and every execution
   * gets a fresh scope, i.e. its own `controllers` map, its own document
   * keydown listener and its own shopify:section:load listener. That multiplied
   * the fetches and broke the play/pause control (an even number of executions
   * toggled `aria-pressed` twice per click, so the button looked dead).
   *
   * So the guard has to live outside this scope. The first execution wins: it
   * initialises every `[data-aso-root]` on the page and keeps listening for
   * sections added later, so nothing is lost by the other executions bailing
   * out. The flag sits on `window`, which survives the theme editor replacing
   * section markup (an already-executed external script is never re-run by that
   * anyway).
   */
  var scope = /** @type {AsoWindow} */ (window);

  if (!scope.__venroyAsoInit) {
    scope.__venroyAsoInit = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
  }
})();
